// In-world aiming reticle: TEX.reticle (or a generated crosshair) at the aim
// point, with a thin arc showing the held weapon's heat (red and pulsing when
// overheated) and an inner arc for charge weapons. The OS cursor is hidden by
// CSS on the game canvas.

import type Phaser from 'phaser';
import { DEPTH } from '../../config/constants';
import { COLORS, mix } from '../../config/palette';
import { TEX } from '../../art/keys';
import { placeholderReticle } from './placeholder';

const HEAT_RADIUS = 21;
const CHARGE_RADIUS = 15;
/** Arc starts at the top and runs clockwise. */
const ARC_START = -Math.PI / 2;
/** Values closer than this to the drawn ones skip the redraw. */
const EPS = 0.004;

export class Reticle {
  private readonly img: Phaser.GameObjects.Image;
  private readonly arcs: Phaser.GameObjects.Graphics;
  private drawnHeat = -1;
  private drawnCharge = -1;
  private drawnHot = false;
  private drawnColor = -1;
  private pulse = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene, color: number) {
    const key = scene.textures.exists(TEX.reticle) ? TEX.reticle : placeholderReticle(scene);
    this.img = scene.add.image(0, 0, key).setDepth(DEPTH.WORLD_UI).setTint(mix(color, 0xffffff, 0.35));
    this.arcs = scene.add.graphics().setDepth(DEPTH.WORLD_UI);
  }

  /**
   * heat/charge in 0..1. Called every frame; the arc graphics only redraw when
   * a value visibly changed.
   */
  update(dt: number, x: number, y: number, heat: number, overheated: boolean, charge: number, color: number): void {
    if (this.destroyed) return;
    this.img.setPosition(x, y);
    this.arcs.setPosition(x, y);
    this.pulse += dt;
    const hot = overheated;
    if (hot) {
      // Pulsing red while the weapon is locked out.
      const k = 0.5 + 0.5 * Math.sin(this.pulse * 18);
      this.img.setTint(mix(COLORS.threat, 0xffffff, 0.25 * k));
      this.arcs.setAlpha(0.65 + 0.35 * k);
    } else if (this.drawnHot) {
      this.img.setTint(mix(color, 0xffffff, 0.35));
      this.arcs.setAlpha(1);
    }
    const heatQ = Math.max(0, Math.min(1, heat));
    const chargeQ = Math.max(0, Math.min(1, charge));
    if (
      Math.abs(heatQ - this.drawnHeat) < EPS &&
      Math.abs(chargeQ - this.drawnCharge) < EPS &&
      hot === this.drawnHot &&
      color === this.drawnColor
    ) {
      return;
    }
    if (!hot && this.drawnColor !== color) this.img.setTint(mix(color, 0xffffff, 0.35));
    this.drawnHeat = heatQ;
    this.drawnCharge = chargeQ;
    this.drawnHot = hot;
    this.drawnColor = color;

    const g = this.arcs;
    g.clear();
    if (heatQ > 0.01 || hot) {
      // Track, then the fill: amber → red as it nears the limit.
      g.lineStyle(3, COLORS.ink, 0.55);
      g.beginPath();
      g.arc(0, 0, HEAT_RADIUS, 0, Math.PI * 2);
      g.strokePath();
      const fill = hot ? COLORS.threat : mix(COLORS.heatBar, COLORS.threat, Math.max(0, (heatQ - 0.6) / 0.4));
      g.lineStyle(2.5, fill, 0.95);
      g.beginPath();
      g.arc(0, 0, HEAT_RADIUS, ARC_START, ARC_START + Math.PI * 2 * (hot ? 1 : heatQ), false);
      g.strokePath();
    }
    if (chargeQ > 0.01) {
      g.lineStyle(2.5, chargeQ >= 0.999 ? 0xffffff : color, 1);
      g.beginPath();
      g.arc(0, 0, CHARGE_RADIUS, ARC_START, ARC_START + Math.PI * 2 * chargeQ, false);
      g.strokePath();
    }
  }

  setVisible(v: boolean): void {
    if (this.destroyed) return;
    this.img.setVisible(v);
    this.arcs.setVisible(v);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.img.destroy();
    this.arcs.destroy();
  }
}
